// frontend/src/pages/AdminPanel.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Eye, EyeOff, LogOut, FileText, BarChart2, Image, X, Save, Send, Link2, Home, Settings, CheckCircle2, Clock } from 'lucide-react';

// ── Simple Rich Text toolbar (no external deps) ────────────
function RichEditor({ value, onChange }) {
  const editorRef = useRef(null);

  const exec = (cmd, val = null) => {
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current.innerHTML);
  };

  const handleImageUrl = () => {
    const url = prompt('Enter image URL:');
    if (url) exec('insertImage', url);
  };

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) exec('createLink', url);
  };

  const btnStyle = (active) => ({
    padding: '5px 10px', border: '1px solid var(--border)', background: active ? '#E8F1FF' : 'white',
    borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, fontFamily: 'inherit'
  });

  return (
    <div style={{ border: '2px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{ background: '#f8fafc', padding: '8px 12px', display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        {[['Bold','B',() => exec('bold')], ['Italic','I',() => exec('italic')], ['Underline','U',() => exec('underline')]].map(([title, label, fn]) => (
          <button key={title} title={title} style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); fn(); }}>{label}</button>
        ))}
        <span style={{ color: '#ddd', margin: '0 4px' }}>|</span>
        <button title="H2" style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'h2'); }}>H2</button>
        <button title="H3" style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'h3'); }}>H3</button>
        <button title="Paragraph" style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'p'); }}>¶</button>
        <span style={{ color: '#ddd', margin: '0 4px' }}>|</span>
        <button title="Bullet List" style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }}>• List</button>
        <button title="Numbered List" style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }}>1. List</button>
        <button title="Blockquote" style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'blockquote'); }}>" Quote</button>
        <span style={{ color: '#ddd', margin: '0 4px' }}>|</span>
        <button title="Link" style={{ ...btnStyle(false), display: 'inline-flex', alignItems: 'center', gap: 4 }} onMouseDown={e => { e.preventDefault(); handleLink(); }}><Link2 size={13} /> Link</button>
        <button title="Image" style={{ ...btnStyle(false), display: 'inline-flex', alignItems: 'center', gap: 4 }} onMouseDown={e => { e.preventDefault(); handleImageUrl(); }}><Image size={13} /> Img</button>
        <button title="Code block" style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'pre'); }}>&lt;/&gt; Code</button>
        <span style={{ color: '#ddd', margin: '0 4px' }}>|</span>
        <button title="Clear" style={{ ...btnStyle(false), color: '#ef4444', display: 'inline-flex', alignItems: 'center', gap: 4 }} onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}><X size={13} /> Clear</button>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current.innerHTML)}
        dangerouslySetInnerHTML={{ __html: value }}
        style={{
          minHeight: 320, padding: 20, outline: 'none', fontSize: '0.97rem', lineHeight: 1.75,
          fontFamily: 'DM Sans, sans-serif',
          overflowY: 'auto'
        }}
      />
    </div>
  );
}

// ── Post Form (create/edit) ────────────────────────────────
function PostForm({ post, onSave, onCancel }) {
  const token = localStorage.getItem('token');
  const [form, setForm] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    category: post?.category || 'General',
    tags: post?.tags?.join(', ') || '',
    status: post?.status || 'draft',
  });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(post?.coverImage || '');
  const [saving, setSaving] = useState(false);

  const CATEGORIES = ['General', 'Tutorial', 'Tips & Tricks', 'News', 'Product Update'];

  const set = (k) => (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, [k]: val }));
    if (k === 'title' && !post) {
      setForm(f => ({ ...f, title: val, slug: val.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-') }));
    }
  };

  const handleCover = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async (statusOverride) => {
    if (!form.title || !form.excerpt || !form.content) {
      return toast.error('Title, excerpt and content are required');
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, status: statusOverride || form.status }).forEach(([k, v]) => fd.append(k, v));
      if (coverFile) fd.append('coverImage', coverFile);

      if (post?._id) {
        await axios.put(`/api/blog/${post._id}`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
        toast.success('Post updated!');
      } else {
        await axios.post('/api/blog', fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
        toast.success('Post created!');
      }
      onSave();
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.error) || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.3rem' }}>{post ? 'Edit Post' : 'New Blog Post'}</h2>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={22} /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Title *</label>
          <input value={form.title} onChange={set('title')} placeholder="Post title..." />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Slug (URL)</label>
          <input value={form.slug} onChange={set('slug')} placeholder="auto-generated-from-title" />
        </div>
      </div>

      <div className="form-group">
        <label>Excerpt * <span style={{ color: '#94a3b8', fontWeight: 400 }}>({form.excerpt.length}/300 chars)</span></label>
        <textarea value={form.excerpt} onChange={set('excerpt')} maxLength={300}
          placeholder="Short summary shown in blog list..."
          style={{ width: '100%', padding: '12px 16px', border: '2px solid var(--border)', borderRadius: 10, fontFamily: 'inherit', fontSize: '0.95rem', resize: 'vertical', minHeight: 80, outline: 'none' }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Category</label>
          <select value={form.category} onChange={set('category')} style={{ width: '100%', padding: '12px 16px', border: '2px solid var(--border)', borderRadius: 10, fontFamily: 'inherit', fontSize: '0.95rem', outline: 'none', background: 'white' }}>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Tags <span style={{ color: '#94a3b8', fontWeight: 400 }}>(comma separated)</span></label>
          <input value={form.tags} onChange={set('tags')} placeholder="pdf, tips, tutorial" />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Status</label>
          <select value={form.status} onChange={set('status')} style={{ width: '100%', padding: '12px 16px', border: '2px solid var(--border)', borderRadius: 10, fontFamily: 'inherit', fontSize: '0.95rem', outline: 'none', background: 'white' }}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
      </div>

      {/* Cover image */}
      <div className="form-group">
        <label>Cover Image</label>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '2px dashed var(--border)', borderRadius: 10, padding: '12px 20px', fontSize: '0.88rem', color: 'var(--text-muted)', transition: 'var(--transition)' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            <Image size={18} /> Click to upload cover image
            <input type="file" accept="image/*" onChange={handleCover} style={{ display: 'none' }} />
          </label>
          {coverPreview && (
            <div style={{ position: 'relative' }}>
              <img src={coverPreview} alt="cover" style={{ height: 60, borderRadius: 8, objectFit: 'cover' }} />
              <button onClick={() => { setCoverFile(null); setCoverPreview(''); }}
                style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                ×
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content editor */}
      <div className="form-group">
        <label>Content *</label>
        <RichEditor value={form.content} onChange={v => setForm(f => ({ ...f, content: v }))} />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8, flexWrap: 'wrap' }}>
        <button onClick={onCancel} className="btn btn-outline">Cancel</button>
        <button onClick={() => handleSave('draft')} className="btn btn-secondary" disabled={saving}>
          <Save size={16} /> {saving ? 'Saving...' : 'Save Draft'}
        </button>
        <button onClick={() => handleSave('published')} className="btn btn-primary" disabled={saving}>
          <Send size={16} /> {saving ? 'Publishing...' : 'Publish Post'}
        </button>
      </div>
    </div>
  );
}

// ── Main Admin Panel ───────────────────────────────────────
export default function AdminPanel() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [posts, setPosts]     = useState([]);
  const [stats, setStats]     = useState({});
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState('list');   // 'list' | 'new' | 'edit'
  const [editPost, setEditPost] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [postsRes, statsRes] = await Promise.all([
        axios.get('/api/blog/admin/all', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/stats')
      ]);
      setPosts(postsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/login'); }
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post permanently?')) return;
    setDeleting(id);
    try {
      await axios.delete(`/api/blog/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Post deleted');
      loadData();
    } catch { toast.error('Delete failed'); }
    setDeleting(null);
  };

  const handleToggleStatus = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`/api/blog/${post._id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Post ${newStatus}`);
      loadData();
    } catch { toast.error('Update failed'); }
  };

  const handleLogout = () => { localStorage.removeItem('token'); navigate('/'); };

  if (view === 'new')  return <div style={{ maxWidth: 960, margin: '40px auto', padding: '0 20px' }}><PostForm onSave={() => { loadData(); setView('list'); }} onCancel={() => setView('list')} /></div>;
  if (view === 'edit') return <div style={{ maxWidth: 960, margin: '40px auto', padding: '0 20px' }}><PostForm post={editPost} onSave={() => { loadData(); setView('list'); setEditPost(null); }} onCancel={() => { setView('list'); setEditPost(null); }} /></div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.8rem', fontWeight: 800, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Settings size={26} /> Admin Panel
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Manage blog posts and site content</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link to="/" className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Home size={14} /> Site</Link>
          <Link to="/blogs" className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FileText size={14} /> Blog</Link>
          <button onClick={handleLogout} className="btn btn-sm" style={{ background: '#fee2e2', color: '#dc2626', border: 'none' }}>
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 32 }}>
        {[
          { icon: <FileText size={20} />, label: 'Total Posts', value: posts.length, color: '#005CFC' },
          { icon: <Eye size={20} />, label: 'Published', value: posts.filter(p => p.status === 'published').length, color: '#22c55e' },
          { icon: <EyeOff size={20} />, label: 'Drafts', value: posts.filter(p => p.status === 'draft').length, color: '#f59e0b' },
          { icon: <BarChart2 size={20} />, label: 'Conversions', value: stats.totalConversions || 0, color: '#3b82f6' },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: s.color + '15', color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.icon}</div>
            <div>
              <div style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.6rem', fontWeight: 800 }}>{s.value}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Blog posts list */}
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.1rem' }}>Blog Posts</h2>
          <button onClick={() => setView('new')} className="btn btn-primary btn-sm">
            <Plus size={16} /> New Post
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>
        ) : posts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><FileText size={40} strokeWidth={1.5} /></div>
            <p>No posts yet.</p>
            <button onClick={() => setView('new')} className="btn btn-primary" style={{ marginTop: 16 }}>
              <Plus size={16} /> Write First Post
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  {['Title', 'Category', 'Status', 'Views', 'Date', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--text-muted)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {posts.map(post => (
                  <tr key={post._id} style={{ borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '14px 12px', maxWidth: 280 }}>
                      <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{post.title}</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>/blogs/{post.slug}</div>
                    </td>
                    <td style={{ padding: '14px 12px' }}>
                      <span style={{ background: '#f3f4f6', padding: '3px 10px', borderRadius: 50, fontSize: '0.78rem' }}>{post.category}</span>
                    </td>
                    <td style={{ padding: '14px 12px' }}>
                      <button onClick={() => handleToggleStatus(post)}
                        style={{ background: post.status === 'published' ? '#dcfce7' : '#fef9c3',
                          color: post.status === 'published' ? '#15803d' : '#854d0e',
                          border: 'none', borderRadius: 50, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        {post.status === 'published' ? <><CheckCircle2 size={13} /> Published</> : <><Clock size={13} /> Draft</>}
                      </button>
                    </td>
                    <td style={{ padding: '14px 12px', color: 'var(--text-muted)' }}>{post.views || 0}</td>
                    <td style={{ padding: '14px 12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(post.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                    </td>
                    <td style={{ padding: '14px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {post.status === 'published' && (
                          <a href={`/blogs/${post.slug}`} target="_blank" rel="noreferrer"
                            style={{ padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-muted)', textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                            title="View post">
                            <Eye size={14} />
                          </a>
                        )}
                        <button onClick={() => { setEditPost(post); setView('edit'); }}
                          style={{ padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, background: 'white', cursor: 'pointer', color: '#3b82f6', display: 'flex', alignItems: 'center' }}
                          title="Edit post">
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(post._id)} disabled={deleting === post._id}
                          style={{ padding: '5px 8px', border: '1px solid #fca5a5', borderRadius: 6, background: '#fff0f0', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}
                          title="Delete post">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
